import React, { useContext, useState } from 'react';

import { CreateContractDealsInput } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import DatePicker from '@amzn/meridian/date-picker';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { isFuture, isLastDayOfMonth, parseISO } from 'date-fns';
import { isEmpty } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { END_DATE_AFTER_START_DATE, IS_REQUIRED } from 'src/constants/errorMessages';
import { AppContext } from 'src/contexts/appContext';
import { IDealsRowData } from 'src/types/IDeals';
import { IToast } from 'src/types/IToaster';
import { convertDateStringToDateObj, convertTimestampToYYYYMMDDInUTCTimezone } from 'src/util/dateTimeUtil';

interface IEndDateDealModalProps {
    deal?: IDealsRowData;
    isOpen: boolean;
    onClose: (refreshData: boolean) => void;
}

export const EndDateDealModal = ({ deal, isOpen, onClose }: IEndDateDealModalProps) => {
    const [dealEndDate, setDealEndDate] = useState('');
    const [dealEndDateErrMsg, setDealEndDateErrMsg] = useState('');
    const isDisabledDate = (dateStr: string) => !(isLastDayOfMonth(parseISO(dateStr)) && isFuture(parseISO(dateStr)));
    const { userInfo, setToasts, setTopAlertMsg } = useContext(AppContext);
    const widths = ['25%', '75%'];

    const handleEndDateDeal = () => {
        if (!deal) return;

        const input = CreateContractDealsInput.clone({
            modifiedDeals: [
                {
                    entityContractId: deal.entityContractId!,
                    endDate: convertDateStringToDateObj(dealEndDate)
                }
            ],
            createdBy: userInfo.username
        });

        contractConfigurationClient.createContractDeals(input).subscribe(
            () => {
                const toast: IToast = {
                    id: 'end-date-deal-toast',
                    timeout: DEFAULT_TOASTER_TIMEOUT,
                    message: 'Deal end dated'
                };
                setToasts([toast]);
                handleOnClose(true);
            },
            (error: UnknownCoralError) => {
                // eslint-disable-next-line no-console
                console.error('CeateContractDeals Unexpected error', error.receivedException.message);
                setTopAlertMsg(error.receivedException.message);
            }
        );
    };

    const handleOnClose = (refreshData = false) => {
        setDealEndDate('');
        setDealEndDateErrMsg('');
        onClose(refreshData);
    };

    const handleEndDateOnBlur = () => {
        if (isEmpty(dealEndDate)) {
            setDealEndDateErrMsg(IS_REQUIRED);
        } else if (deal && convertDateStringToDateObj(dealEndDate) < deal.startDate!) {
            setDealEndDateErrMsg(END_DATE_AFTER_START_DATE);
        } else {
            setDealEndDateErrMsg('');
        }
    };

    return (
        <Modal
            title="End date deal"
            open={isOpen}
            onClose={handleOnClose}
            scrollContainer="viewport"
            closeLabel="Close"
            width="500px"
        >
            <Column spacing="300">
                <Row widths={widths}>
                    <Text type="h100">Vendor</Text>
                    <Text>{deal?.entityName ?? ''}</Text>
                </Row>
                <Row widths={widths}>
                    <Text type="h100">Service</Text>
                    <Text>{deal?.serviceName ?? ''}</Text>
                </Row>
                <Row widths={widths}>
                    <Text type="h100">Contract</Text>
                    <Text>{deal?.contractName ?? ''}</Text>
                </Row>
                <Row widths={widths}>
                    <Text type="h100">Start date</Text>
                    <Text>{convertTimestampToYYYYMMDDInUTCTimezone(deal?.startDate ?? new Date())}</Text>
                </Row>
                <Row widths={widths}>
                    <Text type="h100">End date</Text>
                    <DatePicker
                        value={dealEndDate}
                        onChange={setDealEndDate}
                        disabledDates={isDisabledDate}
                        locale="en-CA"
                        error={!isEmpty(dealEndDateErrMsg)}
                        errorMessage={dealEndDateErrMsg}
                        onBlur={handleEndDateOnBlur}
                        data-testid="end-date-modal-date-picker"
                    />
                </Row>
            </Column>
            <ModalFooter>
                <Row alignmentHorizontal="end" widths="fit">
                    <Button type="secondary" size="small" onClick={handleOnClose}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        onClick={handleEndDateDeal}
                        disabled={!isEmpty(dealEndDateErrMsg) || isEmpty(dealEndDate)}
                    >
                        Yes, end date it
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};
